import {
  Bar,
  BarChart,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { BarShapeProps } from 'recharts/types/cartesian/Bar';
import type { ReactNode } from 'react';
import type { TooltipProps } from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { chartColors } from '@/design/tokens';
import { cn } from '@/lib/utils';
import { formatThousandsTick } from '@/lib/format';
import { ChartTooltip } from '@/components/charts/ChartTooltip';

interface ChartDatum {
  label: string;
  [key: string]: string | number;
}

interface Props {
  data: ChartDatum[];
  title: string;
  description: ReactNode;
  dataKey?: string;
  name?: string;
  fill?: string;
  fillKey?: string;
  className?: string;
  barSize?: number;
  yAxisWidth?: number;
  xTickSize?: number;
  yAllowDecimals?: boolean;
  yTickFormatter?: (value: number) => string;
  tooltipContent?: TooltipProps<ValueType, NameType>['content'];
}

export function ReportBarChart({
  data,
  title,
  description,
  dataKey = 'amount',
  name = 'Сумма',
  fill = chartColors.primary,
  fillKey,
  className,
  barSize,
  yAxisWidth = 50,
  xTickSize = 10,
  yAllowDecimals,
  yTickFormatter = formatThousandsTick,
  tooltipContent = <ChartTooltip />,
}: Props) {
  const resolvedBarSize =
    barSize ?? Math.max(8, Math.min(28, Math.floor(300 / (data.length || 1))));

  return (
    <div className={cn('p-5 rounded-2xl border border-border/30 bg-card', className)}>
      <div className='text-sm font-semibold text-foreground mb-1'>{title}</div>
      <div className='text-xs text-muted-foreground mb-3'>{description}</div>
      <ResponsiveContainer width='100%' height={200}>
        <BarChart data={data} barSize={resolvedBarSize}>
          <CartesianGrid strokeDasharray='3 3' stroke={chartColors.border} vertical={false} />
          <XAxis
            dataKey='label'
            tick={{ fontSize: xTickSize, fill: chartColors.text }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            allowDecimals={yAllowDecimals}
            tick={{ fontSize: 11, fill: chartColors.text }}
            axisLine={false}
            tickLine={false}
            width={yAxisWidth}
            tickFormatter={yTickFormatter}
          />
          <Tooltip content={tooltipContent} cursor={false} />
          <Bar
            dataKey={dataKey}
            name={name}
            fill={fill}
            radius={[6, 6, 0, 0]}
            isAnimationActive
            animationDuration={700}
            animationEasing='ease-out'
            shape={
              fillKey
                ? (props: BarShapeProps) => (
                    <Rectangle
                      {...props}
                      fill={String(data[props.index ?? 0]?.[fillKey] ?? fill)}
                    />
                  )
                : undefined
            }
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
